import { database } from '../firebase';
import { ref, get, set } from 'firebase/database';

// ============================================
// MEMORY HIGHWAY SYSTEM
// ============================================

class MemoryHighway {
  constructor(userId, projects) {
    this.userId = userId;
    this.projects = projects;

    // Memory Banks
    this.STM = {           // Short-Term Memory Cache
      activeGoals: [],
      currentConstraints: [],
      recentDecisions: [],
      openThreads: [],
      lastRefresh: null
    };
    this.PMB = [];         // Personal Memory Bank
    this.ECB = {};         // Entity/Company Banks (per project)
    this.CL = [];          // Conversation Log

    this.loaded = false;
  }

  // HIGHWAY 0: Load all memory banks from Firebase
  async loadFromFirebase() {
    if (!this.userId) return;

    try {
      // Load Personal Memory Bank
      const pmbRef = ref(database, `users/${this.userId}/personalProfileBank`);
      const pmbSnap = await get(pmbRef);
      this.PMB = pmbSnap.val() || [];

      // Load Entity Banks for each project
      for (const project of this.projects) {
        const ecbRef = ref(database, `users/${this.userId}/projects/${project.id}/dataBank`);
        const ecbSnap = await get(ecbRef);
        this.ECB[project.id] = ecbSnap.val() || [];
      }

      // Load Conversation Log
      const clRef = ref(database, `users/${this.userId}/conversationLog`);
      const clSnap = await get(clRef);
      this.CL = clSnap.val() || [];

      this.loaded = true;
      this.refreshSTM();
    } catch (e) {
      console.error('Failed to load memory banks:', e);
    }
  }

  // HIGHWAY 0b: Additively update the project list without discarding loaded banks.
  // Replaces this.projects, loads ECB only for previously-unseen project ids, and
  // refreshes STM. NEVER deletes existing ECB keys, NEVER touches PMB or CL, NEVER writes.
  async updateProjects(projects) {
    this.projects = projects || [];

    if (this.userId) {
      try {
        for (const project of this.projects) {
          if (project.id && !(project.id in this.ECB)) {
            const ecbRef = ref(database, `users/${this.userId}/projects/${project.id}/dataBank`);
            const ecbSnap = await get(ecbRef);
            this.ECB[project.id] = ecbSnap.val() || [];
          }
        }
      } catch (e) {
        console.error('Failed to load entity banks for new projects:', e);
      }
    }

    this.refreshSTM();
  }

  // HIGHWAY 1: Refresh Short-Term Memory from recent data
  refreshSTM() {
    const now = Date.now();
    const recentThreshold = 7 * 24 * 60 * 60 * 1000; // 7 days

    // Collect active goals from all projects
    this.STM.activeGoals = this.projects.flatMap(p =>
      (p.goals || [])
        .filter(g => !g.completed)
        .map(g => ({ ...g, projectId: p.id, projectName: p.name }))
    ).slice(0, 10);

    // Collect recent decisions from PMB
    this.STM.recentDecisions = this.PMB
      .filter(m => m.insights?.some(i => i.type === 'decision'))
      .slice(-5)
      .map(m => m.insights.find(i => i.type === 'decision')?.text);

    // Collect open threads from conversation log
    this.STM.openThreads = this.CL
      .filter(c => now - c.timestamp < recentThreshold)
      .slice(-10)
      .map(c => ({ role: c.role, summary: c.content?.slice(0, 100) }));

    // Collect constraints/preferences from PMB
    this.STM.currentConstraints = this.PMB
      .filter(m => m.insights?.some(i => i.type === 'preference'))
      .slice(-5)
      .map(m => m.insights.find(i => i.type === 'preference')?.text);

    this.STM.lastRefresh = now;
  }

  // HIGHWAY 0: Append to Conversation Log (immutable)
  async appendToConversationLog(role, content) {
    const entry = {
      role,
      content,
      timestamp: Date.now(),
      turnIndex: this.CL.length
    };

    this.CL.push(entry);

    // Persist to Firebase
    if (this.userId) {
      try {
        await set(ref(database, `users/${this.userId}/conversationLog`), this.CL);
      } catch (e) {
        console.error('Failed to save conversation log:', e);
      }
    }

    return entry;
  }

  // HIGHWAY 2: Write to Personal Memory Bank (distilled only)
  async writeToPersonalMemory(candidate) {
    if (!this.qualifyMemoryCandidate(candidate)) return false;

    const entry = {
      type: candidate.type,
      summary: candidate.summary,
      insights: candidate.insights || [],
      sourceTimestamp: Date.now(),
      sourceTurn: this.CL.length - 1
    };

    this.PMB.push(entry);

    // Persist to Firebase
    if (this.userId) {
      try {
        await set(ref(database, `users/${this.userId}/personalProfileBank`), this.PMB);
      } catch (e) {
        console.error('Failed to save PMB:', e);
      }
    }

    this.refreshSTM();
    return true;
  }

  // HIGHWAY 3: Write to Entity Memory Bank (distilled only)
  async writeToEntityMemory(entityId, candidate) {
    if (!entityId || !this.qualifyMemoryCandidate(candidate)) return false;

    if (!this.ECB[entityId]) {
      this.ECB[entityId] = [];
    }

    const entry = {
      type: candidate.type,
      data: candidate.data,
      summary: candidate.summary,
      timestamp: Date.now(),
      sourceTurn: this.CL.length - 1,
      aiContext: candidate.aiContext
    };

    this.ECB[entityId].push(entry);

    // Persist to Firebase
    if (this.userId) {
      try {
        await set(ref(database, `users/${this.userId}/projects/${entityId}/dataBank`), this.ECB[entityId]);
      } catch (e) {
        console.error('Failed to save ECB:', e);
      }
    }

    this.refreshSTM();
    return true;
  }

  // Memory Quality Gate
  qualifyMemoryCandidate(candidate) {
    if (!candidate) return false;
    if (!candidate.summary && !candidate.data) return false;
    if (candidate.summary?.length < 10) return false;
    return true;
  }

  // HIGHWAY 4: Build context for AI from STM + targeted lookups
  buildContextForAI() {
    const context = {
      // From STM
      activeGoals: this.STM.activeGoals.map(g =>
        `[${g.projectName}] ${g.title}${g.completed ? ' (done)' : ''}`
      ),
      recentDecisions: this.STM.recentDecisions.filter(Boolean),
      userPreferences: this.STM.currentConstraints.filter(Boolean),

      // Project summaries
      projectSummaries: this.projects.map(p => ({
        id: p.id,
        name: p.name,
        goalsCount: p.goals?.length || 0,
        completedGoals: p.goals?.filter(g => g.completed).length || 0,
        tasksCount: p.todos?.length || 0,
        completedTasks: p.todos?.filter(t => t.completed).length || 0
      })),

      // Recent conversation context
      recentConversation: this.CL.slice(-5).map(c =>
        `${c.role}: ${c.content?.slice(0, 150)}`
      )
    };

    return context;
  }

  // Get entity-specific context
  getEntityContext(entityId) {
    const bank = this.ECB[entityId] || [];
    return bank.slice(-20).map(e => ({
      type: e.type,
      summary: e.summary || e.data?.title || JSON.stringify(e.data).slice(0, 100)
    }));
  }

  // Normalize a spoken/transcribed name for fuzzy matching (strips filler words,
  // maps spoken numbers, collapses to alphanumerics).
  _normalizeName(s) {
    return String(s)
      .toLowerCase()
      .replace(/\b(the|project|projects|app|my|a|an)\b/g, ' ')
      .replace(/\bten\b/g, '10')
      .replace(/\bby\b/g, 'x')
      .replace(/[^a-z0-9]+/g, '');
  }

  // Standard Levenshtein edit distance (no deps).
  _levenshtein(a, b) {
    const m = a.length;
    const n = b.length;
    if (m === 0) return n;
    if (n === 0) return m;

    let prev = new Array(n + 1);
    let curr = new Array(n + 1);
    for (let j = 0; j <= n; j++) prev[j] = j;

    for (let i = 1; i <= m; i++) {
      curr[0] = i;
      for (let j = 1; j <= n; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        curr[j] = Math.min(
          prev[j] + 1,      // deletion
          curr[j - 1] + 1,  // insertion
          prev[j - 1] + cost // substitution
        );
      }
      const tmp = prev;
      prev = curr;
      curr = tmp;
    }

    return prev[n];
  }

  // Resolve a spoken/transcribed project name to a project via a 5-tier matcher.
  // Returns { project, candidates }: a single match returns { project, candidates: [] };
  // ambiguity (>1 hit at a tier) returns { project: null, candidates: names } and is
  // never guessed; no match falls through to the next tier.
  resolveProject(spokenName) {
    // T0 raw exact (the current find, FIRST hit) — runs BEFORE normalization so an
    // exact name resolves byte-identically to today's behavior even when several
    // project names normalize identically or a name normalizes to the empty string.
    const raw = String(spokenName).toLowerCase();
    const exact = this.projects.find(p => p.name && p.name.toLowerCase() === raw);
    if (exact) return { project: exact, candidates: [] };

    const norm = this._normalizeName(spokenName);
    const entries = this.projects
      .filter(p => p.name)
      .map(p => ({ p, n: this._normalizeName(p.name) }));

    if (!norm || !entries.length) return { project: null, candidates: [] };

    // T1 normalized exact
    const t1 = entries.filter(e => e.n === norm);
    if (t1.length === 1) return { project: t1[0].p, candidates: [] };
    if (t1.length > 1) return { project: null, candidates: t1.map(e => e.p.name) };

    // T2 containment
    const t2 = entries.filter(e =>
      (norm.length >= 2 && e.n.includes(norm)) ||
      (e.n.length >= 2 && norm.includes(e.n))
    );
    if (t2.length === 1) return { project: t2[0].p, candidates: [] };
    if (t2.length > 1) return { project: null, candidates: t2.map(e => e.p.name) };

    // T3 digit signature
    const digits = (s) => (s.match(/\d/g) || []).join('');
    const normDigits = digits(norm);
    if (normDigits) {
      const t3 = entries.filter(e => digits(e.n) === normDigits);
      if (t3.length === 1) return { project: t3[0].p, candidates: [] };
      if (t3.length > 1) return { project: null, candidates: t3.map(e => e.p.name) };
    }

    // T4 levenshtein
    const scored = entries
      .map(e => ({ e, d: this._levenshtein(norm, e.n) }))
      .sort((a, b) => a.d - b.d);
    const best = scored[0];
    const limit = Math.max(2, Math.floor(Math.max(norm.length, best.e.n.length) / 4));
    const within = scored.filter(s => s.d <= limit);
    if (within.length === 1) return { project: within[0].e.p, candidates: [] };
    if (within.length > 1) {
      return { project: null, candidates: within.map(s => s.e.p.name) };
    }

    return { project: null, candidates: [] };
  }

  // HIGHWAY 4b: Get comprehensive project knowledge from Entity Memory Bank
  getProjectKnowledge(projectName) {
    // Resolve the project (fuzzy-matches transcribed/spoken names).
    const { project, candidates } = this.resolveProject(projectName);

    if (!project) {
      const names = this.projects.map(p => p.name).filter(Boolean);
      const hint = candidates.length ? `Closest matches: ${candidates.join(', ')}. ` : '';
      return {
        found: false,
        summary: `I couldn't find a project matching "${projectName}". ${hint}The user's projects are: ${names.join(', ') || '(none)'}. If one of these matches what the user meant, call get_project_knowledge again with that exact name.`
      };
    }

    const entityBank = this.ECB[project.id] || [];

    // Analyze the entity bank events
    const eventCounts = {};
    const recentEvents = [];
    const completedItems = [];
    const addedItems = [];
    const decisions = [];

    entityBank.forEach(event => {
      // Count event types
      eventCounts[event.type] = (eventCounts[event.type] || 0) + 1;

      // Categorize events
      if (event.type.includes('COMPLETED')) {
        completedItems.push({
          item: event.aiContext?.item || event.data?.title || event.data?.task,
          when: event.timestamp
        });
      } else if (event.type.includes('CREATED') || event.type.includes('ADDED')) {
        addedItems.push({
          item: event.aiContext?.item || event.data?.title || event.data?.task,
          when: event.timestamp
        });
      } else if (event.type === 'AI_QUESTION_ANSWERED') {
        decisions.push({
          question: event.data?.question,
          answer: event.data?.answer
        });
      }

      // Track recent events (last 10)
      if (recentEvents.length < 10) {
        recentEvents.push({
          type: event.type,
          item: event.aiContext?.item || event.summary,
          when: event.timestamp
        });
      }
    });

    // Sort recent events by time (newest first)
    recentEvents.sort((a, b) => b.when - a.when);

    // Build knowledge summary
    const goalsCount = project.goals?.length || 0;
    const completedGoals = project.goals?.filter(g => g.completed).length || 0;
    const tasksCount = project.todos?.length || 0;
    const completedTasks = project.todos?.filter(t => t.completed).length || 0;

    // Format time helper
    const formatTimeAgo = (ts) => {
      if (!ts) return '';
      const diff = Date.now() - ts;
      const days = Math.floor(diff / 86400000);
      if (days > 0) return `${days} days ago`;
      const hours = Math.floor(diff / 3600000);
      if (hours > 0) return `${hours} hours ago`;
      return 'recently';
    };

    // Build human-readable summary
    let summary = `Here's what I know about ${project.name}:\n\n`;

    // Current status
    summary += `📊 Current Status:\n`;
    summary += `- ${completedGoals} of ${goalsCount} objectives completed\n`;
    summary += `- ${completedTasks} of ${tasksCount} tasks done\n`;
    summary += `- ${entityBank.length} total events recorded in the Entity Memory Bank\n\n`;

    // Active objectives
    const activeGoals = project.goals?.filter(g => !g.completed) || [];
    if (activeGoals.length > 0) {
      summary += `🎯 Active Objectives:\n`;
      activeGoals.slice(0, 5).forEach(g => {
        summary += `- ${g.title}\n`;
      });
      summary += '\n';
    }

    // Recent activity
    if (recentEvents.length > 0) {
      summary += `📜 Recent Activity:\n`;
      recentEvents.slice(0, 5).forEach(e => {
        const ago = formatTimeAgo(e.when);
        summary += `- ${e.type.replace(/_/g, ' ')}: ${e.item}${ago ? ` (${ago})` : ''}\n`;
      });
      summary += '\n';
    }

    // Key decisions from AI questions
    if (decisions.length > 0) {
      summary += `💡 Key Decisions Recorded:\n`;
      decisions.slice(0, 3).forEach(d => {
        summary += `- Q: ${d.question}\n  A: ${d.answer}\n`;
      });
      summary += '\n';
    }

    // Completed items summary
    if (completedItems.length > 0) {
      summary += `✅ Recently Completed:\n`;
      completedItems.slice(-5).reverse().forEach(c => {
        summary += `- ${c.item}\n`;
      });
    }

    return {
      found: true,
      projectId: project.id,
      projectName: project.name,
      summary,
      stats: {
        totalEvents: entityBank.length,
        goalsCount,
        completedGoals,
        tasksCount,
        completedTasks,
        eventCounts
      },
      recentEvents,
      decisions
    };
  }
}

export default MemoryHighway;
